/* Wedding Style step — 2-level selection card grid used inside the couple
 * questionnaire flow. Value shape when saved on the parent form:
 *
 *   {
 *     category: "south_indian",
 *     sub_style: "tamil_brahmin",
 *     custom_description: null,
 *   }
 *   // OR — interfaith:
 *   {
 *     category: "interfaith_mixed",
 *     primary_style:   { category: "south_indian", sub_style: "tamil_brahmin" },
 *     secondary_style: { category: "sikh_punjabi", sub_style: "punjabi" },
 *   }
 *   // OR — other:
 *   {
 *     category: "other",
 *     custom_description: "Traditional Tamil wedding with a modern reception."
 *   }
 *
 * We keep the copy inspirational, not demographic. Category is a "visual
 * direction" — nothing about religion/caste is presented to the user.
 */
import React, { useMemo, useState } from "react";
import { ChevronLeft, Check, Users, Sparkles, Info } from "lucide-react";

const CATEGORIES = [
    { key: "north_indian",          label: "North Indian",           tag: "Regal, layered, warm palettes" },
    { key: "south_indian",          label: "South Indian",           tag: "Silks, jasmine, mandapam elegance" },
    { key: "christian",             label: "Christian",              tag: "Church aisle, gown, ivory" },
    { key: "muslim",                label: "Muslim",                 tag: "Modest elegance, jewel tones" },
    { key: "sikh_punjabi",          label: "Sikh / Punjabi",         tag: "Anand karaj, high-energy palette" },
    { key: "regional_cultural",     label: "Regional / Cultural",    tag: "Bengali, Kashmiri, Assamese…" },
    { key: "modern_contemporary",   label: "Modern / Contemporary",  tag: "Editorial, minimal, fusion" },
    { key: "interfaith_mixed",      label: "Interfaith / Mixed",     tag: "Blend two traditions" },
    { key: "other",                 label: "Other",                  tag: "Describe your own style" },
];

const SUBSTYLES = {
    north_indian: [
        { key: "punjabi", label: "Punjabi" },
        { key: "rajasthani", label: "Rajasthani" },
        { key: "gujarati", label: "Gujarati" },
        { key: "bengali", label: "Bengali" },
        { key: "marathi", label: "Marathi" },
        { key: "other_north_indian", label: "Other North Indian" },
    ],
    south_indian: [
        { key: "tamil", label: "Tamil" },
        { key: "tamil_brahmin", label: "Tamil Brahmin" },
        { key: "telugu", label: "Telugu" },
        { key: "kannada", label: "Kannada" },
        { key: "malayali", label: "Malayali" },
        { key: "other_south_indian", label: "Other South Indian" },
    ],
    christian: [
        { key: "traditional_indian_christian", label: "Traditional Indian Christian" },
        { key: "western", label: "Western / Church" },
        { key: "contemporary", label: "Contemporary Christian" },
        { key: "other_christian", label: "Other Christian" },
    ],
    muslim: [
        { key: "traditional", label: "Traditional" },
        { key: "regional_muslim", label: "Regional Muslim" },
        { key: "contemporary_muslim", label: "Contemporary Muslim" },
        { key: "other_muslim", label: "Other Muslim" },
    ],
    sikh_punjabi: [
        { key: "traditional_sikh", label: "Traditional Sikh" },
        { key: "punjabi", label: "Punjabi" },
        { key: "contemporary_punjabi", label: "Contemporary Punjabi" },
    ],
    regional_cultural: [
        { key: "bengali", label: "Bengali" },
        { key: "marathi", label: "Marathi" },
        { key: "gujarati", label: "Gujarati" },
        { key: "rajasthani", label: "Rajasthani" },
        { key: "kashmiri", label: "Kashmiri" },
        { key: "assamese", label: "Assamese" },
        { key: "odia", label: "Odia" },
        { key: "other", label: "Other" },
    ],
    modern_contemporary: [
        { key: "modern_indian", label: "Modern Indian" },
        { key: "minimal_elegant", label: "Minimal / Elegant" },
        { key: "luxury_editorial", label: "Luxury / Editorial" },
        { key: "fusion", label: "Fusion" },
        { key: "western_contemporary", label: "Western Contemporary" },
    ],
};

function CategoryCard({ label, tag, selected, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={selected}
            className={`group flex flex-col items-start rounded-[20px] border p-5 text-left transition
                ${selected
                    ? "border-gold bg-gold/10 shadow-soft"
                    : "border-charcoal/10 bg-white hover:border-charcoal/25 hover:shadow-soft"}`}
        >
            <span className="font-serif text-[19px] leading-tight text-charcoal">{label}</span>
            <span className="mt-2 text-[12.5px] leading-snug text-ink">{tag}</span>
            <span
                className={`mt-4 inline-flex h-6 w-6 items-center justify-center rounded-full transition
                    ${selected ? "bg-gold text-charcoal" : "bg-charcoal/5 text-transparent group-hover:text-charcoal/40"}`}
                aria-hidden="true"
            >
                <Check className="h-3.5 w-3.5" />
            </span>
        </button>
    );
}

function SubChip({ label, selected, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={selected}
            className={`rounded-full border px-4 py-2 text-[13px] transition
                ${selected
                    ? "border-gold bg-gold text-charcoal font-medium"
                    : "border-charcoal/15 bg-white text-charcoal hover:border-charcoal/40"}`}
        >
            {label}
        </button>
    );
}

/** Compact interfaith picker — two dropdowns for primary + secondary. */
function InterfaithPicker({ value, onChange, testIdPrefix = "wedding-style-interfaith" }) {
    const primary = value?.primary_style || { category: "", sub_style: "" };
    const secondary = value?.secondary_style || { category: "", sub_style: "" };
    const catList = CATEGORIES.filter((c) => !["interfaith_mixed", "other"].includes(c.key));
    const setP = (patch) => onChange({ ...value, primary_style: { ...primary, ...patch } });
    const setS = (patch) => onChange({ ...value, secondary_style: { ...secondary, ...patch } });
    const primarySubs = SUBSTYLES[primary.category] || [];
    const secondarySubs = SUBSTYLES[secondary.category] || [];
    return (
        <div className="grid gap-4 md:grid-cols-2" data-testid={testIdPrefix}>
            {[
                { title: "Primary tradition", val: primary, sub: primarySubs, set: setP, key: "primary" },
                { title: "Secondary tradition", val: secondary, sub: secondarySubs, set: setS, key: "secondary" },
            ].map((col) => (
                <div key={col.key} className="rounded-[18px] bg-champagne/30 p-5 ring-1 ring-charcoal/5">
                    <div className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/60">{col.title}</div>
                    <select
                        data-testid={`${testIdPrefix}-${col.key}-category`}
                        value={col.val.category}
                        onChange={(e) => col.set({ category: e.target.value, sub_style: "" })}
                        className="mt-2 w-full rounded-[10px] border border-charcoal/15 bg-white px-3 py-2 text-[13.5px]"
                    >
                        <option value="">Select a tradition…</option>
                        {catList.map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                    </select>
                    {col.sub.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {col.sub.map((s) => (
                                <SubChip
                                    key={s.key}
                                    label={s.label}
                                    selected={col.val.sub_style === s.key}
                                    onClick={() => col.set({ sub_style: s.key })}
                                    testId={`${testIdPrefix}-${col.key}-${s.key}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function WeddingStyleStep({ value, onChange }) {
    // Local UI state — the "back to categories" button flips this so the
    // customer can reset without losing prior data.
    const [browsing, setBrowsing] = useState(!value?.category);

    const selectedCategory = value?.category || null;
    const selectedSub = value?.sub_style || null;
    const subs = useMemo(() => SUBSTYLES[selectedCategory] || [], [selectedCategory]);
    const selectedLabel = useMemo(() => {
        const c = CATEGORIES.find((x) => x.key === selectedCategory);
        return c ? c.label : "";
    }, [selectedCategory]);

    const pickCategory = (key) => {
        // Interfaith / other never has "sub_style" — different shape.
        if (key === "interfaith_mixed") {
            onChange({ category: "interfaith_mixed", primary_style: null, secondary_style: null });
        } else if (key === "other") {
            onChange({ category: "other", custom_description: value?.custom_description || "" });
        } else {
            onChange({ category: key, sub_style: null });
        }
        setBrowsing(false);
    };

    return (
        <div className="space-y-6" data-testid="wedding-style-step">
            <div>
                <div className="flex items-center gap-2 text-gold">
                    <Sparkles className="h-4 w-4" />
                    <span className="text-[11px] uppercase tracking-[0.24em]">Step · your wedding style</span>
                </div>
                <h2 className="mt-3 font-serif text-3xl leading-tight text-charcoal md:text-[38px] md:leading-[1.12]">
                    What kind of wedding are you planning?
                </h2>
                <p className="mt-3 max-w-2xl text-[14.5px] text-ink">
                    Your wedding style influences your outfits, colours, jewellery, decor and overall visual mood. Choose the style that feels closest to your celebration.
                </p>
            </div>

            {/* LEVEL 1 — category grid */}
            {browsing && (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="wedding-style-categories">
                    {CATEGORIES.map((c) => (
                        <CategoryCard
                            key={c.key}
                            label={c.label}
                            tag={c.tag}
                            selected={selectedCategory === c.key}
                            onClick={() => pickCategory(c.key)}
                            testId={`wedding-style-cat-${c.key}`}
                        />
                    ))}
                </div>
            )}

            {/* LEVEL 2 — sub-style / interfaith / other */}
            {!browsing && selectedCategory && (
                <div className="rounded-[22px] bg-champagne/30 p-6 md:p-8" data-testid="wedding-style-sub-panel">
                    <button
                        type="button"
                        onClick={() => setBrowsing(true)}
                        data-testid="wedding-style-back"
                        className="inline-flex items-center gap-1.5 text-[12px] text-ink transition hover:text-charcoal"
                    >
                        <ChevronLeft className="h-3.5 w-3.5" /> Change wedding style
                    </button>
                    <div className="mt-3 flex items-baseline gap-2">
                        <span className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/60">Selected</span>
                        <span className="font-serif text-[22px] text-charcoal">{selectedLabel}</span>
                    </div>

                    {selectedCategory === "interfaith_mixed" && (
                        <div className="mt-4 space-y-3">
                            <p className="text-[13px] text-ink">Choose the two traditions you want to blend. Vowze will weave them into a single coherent visual story — not alternate between them.</p>
                            <InterfaithPicker value={value} onChange={onChange} />
                        </div>
                    )}

                    {selectedCategory === "other" && (
                        <div className="mt-4">
                            <label className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/60">
                                Tell us about your wedding style
                            </label>
                            <textarea
                                data-testid="wedding-style-other-input"
                                value={value?.custom_description || ""}
                                onChange={(e) => onChange({ category: "other", custom_description: e.target.value })}
                                placeholder="Example: Traditional Tamil wedding with a modern reception."
                                rows={3}
                                className="mt-2 w-full rounded-[14px] border border-charcoal/15 bg-white p-3 text-[13.5px] leading-relaxed focus:border-gold focus:outline-none"
                            />
                        </div>
                    )}

                    {selectedCategory !== "interfaith_mixed" && selectedCategory !== "other" && subs.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2" data-testid="wedding-style-subs">
                            {subs.map((s) => (
                                <SubChip
                                    key={s.key}
                                    label={s.label}
                                    selected={selectedSub === s.key}
                                    onClick={() => onChange({ ...value, category: selectedCategory, sub_style: s.key })}
                                    testId={`wedding-style-sub-${s.key}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-start gap-2 rounded-[14px] bg-charcoal/5 p-4 text-[12px] text-charcoal/70">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-charcoal/50" />
                <span>
                    We use this only as a visual direction — never as a claim about your religion, caste or community. Your personal colour analysis stays primary.
                </span>
            </div>
        </div>
    );
}

/** Validation helper used by the parent form. */
export function isWeddingStyleValid(v) {
    if (!v || typeof v !== "object" || !v.category) return false;
    if (v.category === "interfaith_mixed") {
        return Boolean(v.primary_style?.category && v.secondary_style?.category);
    }
    if (v.category === "other") {
        return Boolean((v.custom_description || "").trim().length >= 8);
    }
    // For every other category a sub_style must be picked.
    return Boolean(v.sub_style);
}
